import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { UsersThree, MagnifyingGlass, ArrowsClockwise, Airplane, Briefcase, Calendar, List } from "@phosphor-icons/react";
import { timeAgo } from "@/lib/format";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};

const CATEGORY_ICON = (cat = "") => {
  const c = cat.toLowerCase();
  if (c.includes("annual") || c.includes("holiday")) return <Airplane size={12} weight="duotone" className="text-[#4f4c90]" />;
  if (c.includes("sick") || c.includes("personal")) return <Briefcase size={12} weight="duotone" className="text-[#DC2626]" />;
  return <Calendar size={12} weight="duotone" className="text-slate-500" />;
};

export default function Team() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("all");
  const [view, setView] = useState("list"); // "list" | "calendar"
  const [syncing, setSyncing] = useState(false);

  const load = () => api.get("/availability").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const items = data?.items || [];
  const filtered = useMemo(() => {
    let list = items;
    if (tab === "on_leave") list = list.filter((x) => x.status === "on_leave");
    if (tab === "available") list = list.filter((x) => x.status === "available");
    if (q.trim()) {
      const needle = q.toLowerCase().trim();
      list = list.filter((x) =>
        (x.full_name || "").toLowerCase().includes(needle)
        || (x.email || "").toLowerCase().includes(needle)
        || (x.job_title || "").toLowerCase().includes(needle)
        || (x.location || "").toLowerCase().includes(needle)
        || (x.leave_category || "").toLowerCase().includes(needle)
      );
    }
    return list;
  }, [items, tab, q]);

  const onLeaveCount = useMemo(() => items.filter((x) => x.status === "on_leave").length, [items]);

  const syncNow = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/availability/sync");
      toast.success(`Synced ${r.data?.stats?.employees ?? 0} employees`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally { setSyncing(false); }
  };

  if (data && !data.connected) {
    return (
      <div className="space-y-4" data-testid="team-page">
        <MyWorkingStatusCard currentUser={user} />
        <div className="max-w-lg mx-auto py-16 text-center" data-testid="team-not-connected">
          <UsersThree size={40} weight="duotone" className="text-slate-400 mx-auto" />
          <h2 className="font-heading text-xl font-semibold mt-3">Not connected to Employment Hero</h2>
          <p className="text-sm text-slate-500 mt-2">Ask an admin to connect it in Settings → Employment Hero HR.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="team-page">
      <MyWorkingStatusCard currentUser={user} />
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2">
            <UsersThree size={22} weight="duotone" className="text-[#4f4c90]" /> Team availability
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {items.length} people · <span className="text-[#DC2626] font-medium">{onLeaveCount} on leave today</span> · last synced {data?.last_sync_at ? timeAgo(data.last_sync_at) : "—"}
          </p>
        </div>
        {isAdmin && (
          <Button onClick={syncNow} disabled={syncing} variant="outline" className="h-9 gap-1 border-slate-200 bg-white" data-testid="team-sync-btn">
            <ArrowsClockwise size={14} className={syncing ? "animate-spin" : ""} /> {syncing ? "Syncing…" : "Sync now"}
          </Button>
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search name, job, location, leave type…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 h-9 bg-white border-slate-200"
            data-testid="team-search"
          />
        </div>
        <div className="flex items-center bg-slate-100 rounded-md p-0.5" data-testid="team-tabs">
          {[
            { key: "all", label: `All (${items.length})` },
            { key: "on_leave", label: `On leave (${onLeaveCount})` },
            { key: "available", label: `Available (${items.length - onLeaveCount})` },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`team-tab-${t.key}`}
              className={`text-xs px-3 py-1.5 rounded transition-colors ${tab === t.key ? "bg-white text-[#4f4c90] font-medium shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex items-center bg-slate-100 rounded-md p-0.5" data-testid="team-view-toggle">
          <button onClick={() => setView("list")} data-testid="team-view-list"
            className={`text-xs px-2 py-1.5 rounded transition-colors flex items-center gap-1 ${view === "list" ? "bg-white text-[#4f4c90] font-medium shadow-sm" : "text-slate-600 hover:text-slate-900"}`}>
            <List size={12} /> List
          </button>
          <button onClick={() => setView("calendar")} data-testid="team-view-calendar"
            className={`text-xs px-2 py-1.5 rounded transition-colors flex items-center gap-1 ${view === "calendar" ? "bg-white text-[#4f4c90] font-medium shadow-sm" : "text-slate-600 hover:text-slate-900"}`}>
            <Calendar size={12} /> Calendar
          </button>
        </div>
      </div>

      {view === "calendar" ? (
        <LeaveCalendar items={filtered} />
      ) : (<div className="hairline rounded-lg bg-white overflow-hidden">
        <div className="grid grid-cols-[1fr_180px_130px_180px] gap-3 px-4 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-medium bg-slate-50 border-b border-slate-200">
          <div>Person</div>
          <div>Job title</div>
          <div>Status</div>
          <div>Details</div>
        </div>
        {filtered.length === 0 && (
          <div className="text-center py-10 text-sm text-slate-500">No matches.</div>
        )}
        {filtered.map((p) => (
          <div key={p.employee_id} className="grid grid-cols-[1fr_180px_130px_180px] gap-3 px-4 py-2.5 items-center border-b border-slate-100 last:border-0 hover:bg-slate-50/60" data-testid={`team-row-${p.employee_id}`}>
            <div className="flex items-center gap-3 min-w-0">
              <Avatar name={p.full_name} url={p.avatar_url} />
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{p.full_name || "—"}</div>
                <div className="text-[11px] text-slate-500 font-mono truncate">{p.email}</div>
              </div>
            </div>
            <div className="text-sm text-slate-700 truncate" title={p.job_title}>{p.job_title || "—"}</div>
            <div>
              {p.status === "on_leave" ? (
                <span className="chip !text-[#DC2626] bg-red-50 !border-red-200 flex items-center gap-1 w-fit">
                  {CATEGORY_ICON(p.leave_category)} On leave
                </span>
              ) : (
                <span className="chip status-in_progress w-fit">Available</span>
              )}
            </div>
            <div className="text-xs text-slate-600">
              {p.status === "on_leave" ? (
                <>
                  <div className="font-medium text-slate-700">{p.leave_category || "Leave"}</div>
                  <div className="text-[11px] text-slate-500 font-mono mt-0.5">Back {fmtDate(p.leave_to)}</div>
                </>
              ) : (
                <span className="text-slate-400 italic text-[11px]">—</span>
              )}
            </div>
          </div>
        ))}
      </div>
      )}
    </div>
  );
}

function LeaveCalendar({ items }) {
  // 6-week window starting on the Monday of the current week.
  const start = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    const dow = (d.getDay() + 6) % 7; // Mon = 0
    d.setDate(d.getDate() - dow);
    return d;
  }, []);
  const DAYS = 42;
  const days = useMemo(() => Array.from({ length: DAYS }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  }), [start]);

  const toKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const today = toKey(new Date());

  // Only include people with leave in the window.
  const rows = useMemo(() => items
    .map((p) => ({
      ...p,
      _requests: (p.leave_requests || []).filter((r) => {
        const s = r.start_date, e = r.end_date;
        return e >= toKey(days[0]) && s <= toKey(days[days.length - 1]);
      }),
    }))
    .filter((p) => p._requests.length > 0), [items, days]);

  const catBar = (cat = "") => {
    const c = cat.toLowerCase();
    if (c.includes("annual") || c.includes("holiday")) return "bg-[#4f4c90]";
    if (c.includes("sick") || c.includes("personal")) return "bg-red-500";
    if (c.includes("parental") || c.includes("mater") || c.includes("pater")) return "bg-pink-500";
    if (c.includes("training") || c.includes("study")) return "bg-blue-500";
    return "bg-slate-500";
  };

  const gridCols = { gridTemplateColumns: `180px repeat(${DAYS}, minmax(28px, 1fr))` };

  return (
    <div className="hairline rounded-lg bg-white overflow-hidden" data-testid="leave-calendar">
      <div className="px-4 py-3 flex items-center justify-between border-b border-slate-100 flex-wrap gap-2">
        <div className="text-[11px] text-slate-500 font-mono">
          {days[0].toLocaleDateString("en-GB", { day: "numeric", month: "short" })} → {days[days.length - 1].toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
        </div>
        <div className="flex items-center gap-3 text-[10px] text-slate-600">
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-[#4f4c90]" /> Annual</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-red-500" /> Sick / Personal</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-pink-500" /> Parental</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-blue-500" /> Training</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-slate-500" /> Other</span>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="text-center py-10 text-sm text-slate-500">No leave scheduled in this window.</div>
      ) : (
        <div className="overflow-x-auto">
          <div className="min-w-[900px]">
            {/* Month label row */}
            <div className="grid bg-slate-50/60" style={gridCols}>
              <div />
              {days.map((d, i) => {
                const showMonth = d.getDate() === 1 || i === 0;
                return (
                  <div key={i} className="text-[10px] uppercase tracking-wider text-slate-500 font-medium px-1 pt-2 pb-0.5">
                    {showMonth ? d.toLocaleDateString("en-GB", { month: "short" }) : ""}
                  </div>
                );
              })}
            </div>

            {/* Day header row */}
            <div className="grid border-b border-slate-200 bg-slate-50/60" style={gridCols}>
              <div />
              {days.map((d, i) => {
                const key = toKey(d);
                const dow = (d.getDay() + 6) % 7; // Mon=0..Sun=6
                const isWeekend = dow >= 5;
                const isToday = key === today;
                return (
                  <div
                    key={i}
                    className={`text-center text-[10px] py-1.5 border-l border-slate-100
                      ${isWeekend ? "bg-slate-100/70 text-slate-500" : "text-slate-600"}
                      ${isToday ? "!bg-[#4f4c90] !text-white font-bold" : ""}
                    `}
                  >
                    <div className="text-[9px] uppercase leading-none">{d.toLocaleDateString("en-GB", { weekday: "narrow" })}</div>
                    <div className="font-medium leading-tight">{d.getDate()}</div>
                  </div>
                );
              })}
            </div>

            {/* Person rows */}
            {rows.map((p) => (
              <div key={p.employee_id} className="relative grid border-b border-slate-100 items-center hover:bg-slate-50/40" style={gridCols} data-testid={`cal-row-${p.employee_id}`}>
                <div className="flex items-center gap-2 px-3 py-2 border-r border-slate-100 bg-white">
                  <Avatar name={p.full_name} url={p.avatar_url} />
                  <div className="min-w-0">
                    <div className="text-xs font-medium truncate">{p.full_name}</div>
                    <div className="text-[10px] text-slate-500 truncate">{p.job_title || ""}</div>
                  </div>
                </div>
                {/* Empty day cells (background + weekend shading) */}
                {days.map((d, i) => {
                  const dow = (d.getDay() + 6) % 7;
                  const isWeekend = dow >= 5;
                  const isToday = toKey(d) === today;
                  return (
                    <div
                      key={i}
                      className={`border-l border-slate-50 h-11
                        ${isWeekend ? "bg-slate-100/60" : ""}
                        ${isToday ? "bg-[#4f4c90]/8" : ""}
                      `}
                    />
                  );
                })}
                {/* Bars */}
                {p._requests.map((r, idx) => {
                  const sIdx = Math.max(0, days.findIndex((d) => toKey(d) >= r.start_date));
                  let eIdx = days.findIndex((d) => toKey(d) > r.end_date);
                  if (eIdx === -1) eIdx = days.length;
                  const span = eIdx - sIdx;
                  if (span <= 0) return null;
                  return (
                    <div
                      key={idx}
                      className={`absolute text-[10px] leading-none flex items-center justify-center rounded-md ${catBar(r.category)} text-white shadow-sm px-1.5 truncate ring-1 ring-white/40`}
                      style={{
                        left: `calc(180px + (${sIdx} / ${DAYS}) * (100% - 180px) + 2px)`,
                        width: `calc(${span} / ${DAYS} * (100% - 180px) - 4px)`,
                        top: "50%",
                        transform: "translateY(-50%)",
                        height: "22px",
                      }}
                      title={`${r.category} · ${r.start_date} → ${r.end_date}`}
                    >
                      {span >= 3 ? r.category : ""}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Avatar({ name, url }) {
  const initials = (name || "?").split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]).join("").toUpperCase();
  if (url && url.startsWith("http") && !url.endsWith("avatar.svg")) {
    return <img src={url} alt="" className="w-8 h-8 rounded-full object-cover border border-slate-200" />;
  }
  return (
    <div className="w-8 h-8 rounded-full bg-[#4f4c90]/15 border border-[#4f4c90]/30 flex items-center justify-center text-[11px] font-medium text-[#4f4c90] flex-shrink-0">
      {initials}
    </div>
  );
}


/**
 * MyWorkingStatusCard — compact card at the top of the Team page that
 * lets the current user set their working location and quick "off"
 * date range. Location list comes from settings.it_locations. Empty
 * location list → card still renders so users can set off-days.
 */
function MyWorkingStatusCard({ currentUser }) {
  const [me, setMe] = useState(null);
  const [locations, setLocations] = useState([]);
  const [saving, setSaving] = useState(false);
  const [sigDraft, setSigDraft] = useState("");

  useEffect(() => {
    if (!currentUser) return;
    api.get("/users").then((r) => {
      const found = (r.data || []).find((u) => u.id === currentUser.id);
      setMe(found || currentUser);
    }).catch(() => setMe(currentUser));
    api.get("/settings").then((r) => setLocations(r.data?.it_locations || [])).catch(() => {});
  }, [currentUser]);

  // Signature + Cmd+Enter — editable per-user compose preferences.
  // Signature defaults to "Thanks, {name}" on first visit if blank;
  // user can override to anything, including an empty string (which,
  // combined with the toggle, is just another way to disable).
  const effectiveSignature = me == null
    ? ""
    : (me.email_signature == null ? `Thanks,\n${me.name || ""}` : me.email_signature);
  useEffect(() => { setSigDraft(effectiveSignature); /* eslint-disable-next-line */ }, [me?.email_signature, me?.name]);

  if (!me) return null;

  const save = async (patch) => {
    setSaving(true);
    try {
      const r = await api.patch(`/users/${me.id}`, patch);
      setMe(r.data);
      toast.success("Updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setSaving(false); }
  };

  const now = new Date(); now.setHours(0, 0, 0, 0);
  const isOff = me.off_from && me.off_until
    && new Date(me.off_from) <= now
    && new Date(me.off_until) >= now;

  const sigEnabled = me.email_signature_enabled !== false; // default-on
  const shortcutOn = me.email_shortcut_send === true; // default-off

  return (
    <div className="space-y-3">
    <div className="hairline rounded-lg p-4 bg-white flex items-center gap-4 flex-wrap" data-testid="my-working-status">
      <div className="text-[11px] uppercase text-slate-500 tracking-wider font-medium">My status</div>
      <div className="flex items-center gap-2">
        <label className="text-xs text-slate-500">Location</label>
        <select
          value={me.working_location || ""}
          onChange={(e) => save({ working_location: e.target.value || null })}
          disabled={saving}
          className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs"
          data-testid="my-working-location"
        >
          <option value="">— not set —</option>
          {locations.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
      </div>
      <div className="flex items-center gap-2">
        <label className="text-xs text-slate-500">Off from</label>
        <input
          type="date"
          value={me.off_from ? String(me.off_from).slice(0, 10) : ""}
          onChange={(e) => save({ off_from: e.target.value || null })}
          disabled={saving}
          className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs"
          data-testid="my-off-from"
        />
        <label className="text-xs text-slate-500">to</label>
        <input
          type="date"
          value={me.off_until ? String(me.off_until).slice(0, 10) : ""}
          onChange={(e) => save({ off_until: e.target.value || null })}
          disabled={saving}
          className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs"
          data-testid="my-off-until"
        />
      </div>
      {isOff && (
        <span className="chip !text-[#DC2626] !border-[#DC2626]/40 bg-[#DC2626]/10">Marked off</span>
      )}
    </div>

    <div className="hairline rounded-lg p-4 bg-white space-y-3" data-testid="my-compose-prefs">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-sm font-medium text-slate-800">Email compose preferences</div>
          <div className="text-[11px] text-slate-500 font-mono">Only applies to your account.</div>
        </div>
      </div>

      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4 rounded border-slate-300 text-[#4f4c90]"
          checked={sigEnabled}
          disabled={saving}
          onChange={(e) => save({ email_signature_enabled: e.target.checked })}
          data-testid="pref-signature-enabled"
        />
        <div className="flex-1">
          <div className="text-sm text-slate-800">Add my signature to the compose box</div>
          <div className="text-[11px] text-slate-500">On by default. The text below is injected into every email reply so you can tweak before sending.</div>
        </div>
      </label>
      <textarea
        value={sigDraft}
        onChange={(e) => setSigDraft(e.target.value)}
        onBlur={() => {
          if (sigDraft !== effectiveSignature) {
            save({ email_signature: sigDraft });
          }
        }}
        rows={3}
        disabled={saving || !sigEnabled}
        placeholder={`Thanks,\n${me.name || "Your Name"}`}
        className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-mono disabled:bg-slate-50 disabled:text-slate-400"
        data-testid="pref-signature-text"
      />

      <label className="flex items-start gap-3 cursor-pointer pt-1 border-t border-slate-100">
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4 rounded border-slate-300 text-[#4f4c90]"
          checked={shortcutOn}
          disabled={saving}
          onChange={(e) => save({ email_shortcut_send: e.target.checked })}
          data-testid="pref-shortcut-send"
        />
        <div className="flex-1">
          <div className="text-sm text-slate-800">Use Cmd+Enter (Ctrl+Enter on Windows) to send emails</div>
          <div className="text-[11px] text-slate-500">Off by default. Only sends when replying by email — internal notes are unaffected.</div>
        </div>
      </label>
    </div>
    </div>
  );
}

